import { useContext, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import companyBasedRequest from "../../request/companyBasedRequest";
import PhoneInput from "../common/phoneInput/phoneInput";
import BannerMessage from "../common/bannerMessage/bannerMessage";
import CATEGORY_OPTIONS from "../../constants/categoryOptions";


const ListShopPage = () => {
	const { language } = useContext(CombinedContext);

	const { ref } = useParams();

	const [shopName, setShopName] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [description, setDescription] = useState("");
	const [category, setCategory] = useState("");
	const [subCategory, setSubCategory] = useState("");
	const [categoryOpen, setCategoryOpen] = useState(false);
	const [subCategoryOpen, setSubCategoryOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [banner, setBanner] = useState(null);
	const [otherDescription, setOtherDescription] = useState("");

	const handleSubmit = async () => {
		if (!shopName || !firstName || !lastName || !email || !phone) {
			setBanner({
				type: "error",
				text:
					language === "es"
						? "Por favor completa todos los campos."
						: "Please fill in all required fields.",
			});
			return;
		}
	
		// 🧠 Special flow for "Other"
		if (category === "other") {
			if (!otherDescription) {
				setBanner({
					type: "error",
					text:
						language === "es"
							? "Por favor describe tu negocio o servicio."
							: "Please describe your business or service.",
				});
				return;
			}
		}
	
		setBanner(null);
		setLoading(true);
	
		try {
			// 🟡 OTHER → request category (NO INVITE)
			if (category === "other") {
				await companyBasedRequest.requestNewCategory({
					shopName,
					firstName,
					lastName,
					email,
					phone,
					description: otherDescription,
					referralCode: ref || null,
				});
	
				setBanner({
					type: "success",
					text:
						language === "es"
							? "Gracias. Revisaremos tu solicitud y te contactaremos pronto."
							: "Thanks! We’ll review your request and contact you soon.",
				});
	
				// Clear form
				setShopName("");
				setFirstName("");
				setLastName("");
				setEmail("");
				setPhone("");
				setCategory("");
				setSubCategory("");
				setOtherDescription("");
	
				return; // ⛔ stop here (no invite)
			}
	
			// 🟢 NORMAL → invite shop
			const response = await companyBasedRequest.requestShopInvite({
				shopName,
				firstName,
				lastName,
				email,
				phone,
				category,
				subCategory,
				referralCode: ref || null,
			});
	
			const msg =
				response?.message?.[language] ||
				(language === "es"
					? "Por favor revisa tu correo electrónico para más instrucciones."
					: "Please check your email for further instructions.");
	
			setBanner({ type: "success", text: msg });
	
			// Clear form
			setShopName("");
			setFirstName("");
			setLastName("");
			setEmail("");
			setPhone("");
			setCategory("");
			setSubCategory("");
		} catch (err) {
			console.error("ListShop error:", err);
	
			const msg =
				err?.response?.data?.error?.[language] ||
				err?.response?.data?.message?.[language] ||
				err.message ||
				(language === "es"
					? "No se pudo enviar la solicitud."
					: "Failed to send request.");
	
			setBanner({ type: "error", text: msg });
		} finally {
			setLoading(false);
		}
	};

	const handleCategoryChange = (e) => {
		setCategory(e.target.value);
		setSubCategory(""); // reset subcategory
	};

	const selectedCategory = CATEGORY_OPTIONS.find((c) => c.value === category);
	const availableSubCategories = selectedCategory?.subCategories || [];

	return (
		<div className="container">
			<div className="card" style={{ maxWidth: 400, margin: "40px auto" }}>
				<div className="hd">
					<div>{translations[language].listShop.title}</div>
					<div className="sub">{translations[language].listShop.subtitle}</div>
				</div>

				<div className="bd">
					{/* ✅ Message banner */}
					{banner && <BannerMessage type={banner.type} text={banner.text} />}

					<div className="row" style={{ marginBottom: 10 }}>
						<input
							className="input"
							placeholder={translations[language].listShop.shopName}
							value={shopName}
							onChange={(e) => setShopName(e.target.value)}
						/>
						<input
							className="input"
							placeholder={translations[language].registerShop.step1.firstName}
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
						/>

						<input
							className="input"
							placeholder={translations[language].registerShop.step1.lastName}
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
						/>
					</div>

					<div className="row" style={{ marginBottom: 10 }}>
						<input
							className="input"
							type="email"
							placeholder={translations[language].listShop.email}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
						<PhoneInput businessPhone={phone} setBusinessPhone={setPhone} />
					</div>

					<div className="row" style={{ marginBottom: 10 }}>
						{/* CATEGORY */}
						<div className="category-input-container">
							<button
								type="button"
								className="category-dropdown-btn"
								onClick={() => {
									setCategoryOpen((prev) => !prev);
									setSubCategoryOpen(false);
								}}
							>
								{category
									? language === "es"
										? selectedCategory.labelES
										: selectedCategory.labelEN
									: language === "es"
									? "Tipo de negocio"
									: "Business category"}
								<span className={`arrow ${categoryOpen ? "open" : ""}`}>▼</span>
							</button>

							{categoryOpen && (
								<ul className="category-dropdown-list">
									{CATEGORY_OPTIONS.map((c) => (
										<li
											key={c.value}
											className="category-dropdown-item"
											onClick={() => {
												setCategory(c.value);
												setSubCategory("");
												setCategoryOpen(false);
											}}
										>
											{language === "es" ? c.labelES : c.labelEN}
										</li>
									))}
								</ul>
							)}
						</div>

						{/* SUBCATEGORY */}
						<div className="category-input-container">
							<button type="button" className="category-dropdown-btn" disabled={!category} onClick={() => setSubCategoryOpen((prev) => !prev)}>
								{subCategory
									? language === "es"
										? availableSubCategories.find((s) => s.value === subCategory)?.labelES
										: availableSubCategories.find((s) => s.value === subCategory)?.labelEN
									: language === "es"
									? "Subcategoría"
									: "Subcategory"}
								<span className={`arrow ${subCategoryOpen ? "open" : ""}`}>▼</span>
							</button>

							{subCategoryOpen && (
								<ul className="category-dropdown-list">
									{availableSubCategories.map((sc) => (
										<li
											key={sc.value}
											className="category-dropdown-item"
											onClick={() => {
												setSubCategory(sc.value);
												setSubCategoryOpen(false);
											}}
										>
											{language === "es" ? sc.labelES : sc.labelEN}
										</li>
									))}
								</ul>
							)}
						</div>
					</div>

					{category === "other" && (
						<div className="other-request-box">
							<textarea
							className="input"
							rows={3}
							placeholder={
								language === "es"
								? "Describe brevemente tu negocio o servicio"
								: "Briefly describe your business or service"
							}
							value={otherDescription}
							onChange={(e) => setOtherDescription(e.target.value)}
							/>
						</div>
					)}

					<button
						className="btn brand"
						style={{ width: "100%", marginBottom: 10 }}
						onClick={handleSubmit}
						disabled={loading}
					>
						{loading
							? translations[language].listShop.sending || "Sending..."
							: category === "other"
							? language === "es"
								? "Enviar solicitud"
								: "Submit request"
							: translations[language].listShop.registerShop}
					</button>

					<div className="sub">
						{translations[language].listShop.alreadyHaveAccount} <Link to="/signin">{translations[language].listShop.signIn}</Link>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ListShopPage;
